import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Sparkles, TrendingUp, Users, Star, Trophy, Share2, Clock } from "lucide-react";

const BASE = "";

const PLAN_LABEL = { M: "Mensal", T: "Trimestral", S: "Semestral", A: "Anual" };
const ESTADO_BADGE = {
  PG: "bg-emerald-100 text-emerald-700",
  PE: "bg-amber-100 text-amber-700",
  CA: "bg-rose-100 text-rose-600",
};
const ESTADO_LABEL = { PG: "Activa", PE: "Pendente", CA: "Cancelada" };

function authHeaders() {
  const token = localStorage.getItem("ihtp_token");
  return { headers: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true };
}

function SubscricoesManager() {
  const [estado, setEstado] = useState("");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async (p = page, ps = pageSize) => {
    try {
      setErr(null);
      const { data } = await axios.get(
        `${BASE}/api/dreams/admin/assinaturas?estado=${estado}&q=${encodeURIComponent(q)}&page=${p}&page_size=${ps}`,
        authHeaders());
      setRows(data.assinaturas || []);
      setTotal(data.total || 0);
      setSelected(new Set());
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
  };
  useEffect(() => { load(page, pageSize); /* eslint-disable-next-line */ }, [estado, page, pageSize]);

  const search = () => { setPage(1); load(1, pageSize); };
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const act = async (id, action, payload = {}) => {
    setBusy(id);
    try {
      const { data } = await axios.post(
        `${BASE}/api/dreams/admin/assinaturas/${id}/${action}`, payload, authHeaders());
      if (action !== "cancelar" && action !== "eliminar") {
        alert(data.message || "Feito.");
      }
      await load();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
    finally { setBusy(0); }
  };

  const onAtivar = (a) => {
    if (window.confirm(`Activar a subscrição #${a.ID_Assinatura} de ${a.NomeCompleto} (plano ${PLAN_LABEL[a.Plano] || a.Plano})?`))
      act(a.ID_Assinatura, "ativar");
  };
  const onEstender = (a) => {
    const m = window.prompt("Estender quantos meses? (1-24)", "1");
    if (m === null) return;
    const meses = parseInt(m, 10);
    if (!meses || meses < 1 || meses > 24) { alert("Número de meses inválido."); return; }
    act(a.ID_Assinatura, "estender", { meses });
  };
  const onEditarData = (a) => {
    const atual = a.AtivaAte ? new Date(a.AtivaAte).toISOString().slice(0, 10) : "";
    const nova = window.prompt("Activa até (AAAA-MM-DD):", atual);
    if (nova === null) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(nova.trim())) { alert("Data inválida. Usa o formato AAAA-MM-DD."); return; }
    act(a.ID_Assinatura, "data", { ativa_ate: nova.trim() });
  };
  const onCancelar = (a) => {
    const motivo = window.prompt(`Cancelar a subscrição #${a.ID_Assinatura} de ${a.NomeCompleto}?\nMotivo:`, "Cancelada pelo admin");
    if (motivo === null) return;
    act(a.ID_Assinatura, "cancelar", { motivo });
  };
  const onEliminar = (a) => {
    if (!window.confirm(
      `⚠️ ELIMINAR DEFINITIVAMENTE a subscrição #${a.ID_Assinatura} de ${a.NomeCompleto}?\n\n` +
      `Esta acção apaga a subscrição e os pagamentos associados da base de dados e NÃO PODE ser revertida.`)) return;
    act(a.ID_Assinatura, "eliminar");
  };

  const isEliminable = (a) => (a.Estado === "PE" || a.Estado === "CA") && !a.PagamentosConfirmados;
  const eliminableRows = (rows || []).filter(isEliminable);
  const allSelected = eliminableRows.length > 0 && eliminableRows.every(a => selected.has(a.ID_Assinatura));
  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected(prev => {
      if (allSelected) return new Set();
      const next = new Set(prev);
      eliminableRows.forEach(a => next.add(a.ID_Assinatura));
      return next;
    });
  };

  const onBulkEliminar = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(
      `⚠️ ELIMINAR DEFINITIVAMENTE ${ids.length} subscrição(ões) seleccionada(s)?\n\n` +
      `Esta acção apaga as subscrições e os pagamentos associados da base de dados e NÃO PODE ser revertida.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await axios.post(
        `${BASE}/api/dreams/admin/assinaturas/eliminar-em-massa`, { ids }, authHeaders());
      let msg = data.message || `${data.deleted} eliminada(s).`;
      if (data.skipped && data.skipped.length > 0) {
        const detail = data.skipped.slice(0, 5).map(s => `#${s.id}: ${s.reason}`).join("\n");
        const more = data.skipped.length > 5 ? `\n… e mais ${data.skipped.length - 5}` : "";
        msg += `\n\nIgnoradas:\n${detail}${more}`;
      }
      alert(msg);
      await load();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
    finally { setBulkBusy(false); }
  };

  const fmt = (d) => (d ? new Date(d).toLocaleDateString("pt-PT") : "—");

  return (
    <Card title="Gestão de Subscrições">
      <div className="flex flex-wrap gap-2 mb-3" data-testid="dreams-subs-filters">
        <select value={estado} onChange={(e) => setEstado(e.target.value)}
          className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white"
          data-testid="dreams-subs-filter-estado">
          <option value="">Todos os estados</option>
          <option value="PG">Activas</option>
          <option value="PE">Pendentes</option>
          <option value="CA">Canceladas</option>
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && search()}
          placeholder="Pesquisar nome ou email…"
          className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm flex-1 min-w-[180px]"
          data-testid="dreams-subs-search" />
        <button onClick={search} className="px-3 py-1.5 rounded-lg bg-slate-800 text-white text-sm hover:bg-slate-700"
          data-testid="dreams-subs-search-btn">Pesquisar</button>
      </div>
      {err && <p className="text-rose-500 text-sm">{err}</p>}
      {!rows && !err && <p className="text-slate-400 text-sm">A carregar…</p>}
      {rows && selected.size > 0 && (
        <div className="flex items-center justify-between gap-3 mb-3 p-2 pl-3 rounded-lg bg-rose-50 border border-rose-200"
          data-testid="dreams-bulk-bar">
          <span className="text-sm text-rose-700 font-medium">
            {selected.size} subscrição(ões) seleccionada(s)
          </span>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelected(new Set())}
              className="px-2.5 py-1 rounded-md border border-slate-300 text-xs text-slate-600 hover:bg-white"
              data-testid="dreams-bulk-clear">Limpar</button>
            <button onClick={onBulkEliminar} disabled={bulkBusy}
              className="px-3 py-1.5 rounded-md bg-rose-600 text-white text-xs font-semibold hover:bg-rose-500 disabled:opacity-50"
              data-testid="dreams-bulk-eliminar">
              {bulkBusy ? "A eliminar…" : `Eliminar ${selected.size} em massa`}
            </button>
          </div>
        </div>
      )}
      {rows && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="dreams-subs-table">
            <thead>
              <tr className="text-left text-xs text-slate-500">
                <th className="py-1.5 w-6">
                  <input type="checkbox" checked={allSelected}
                    onChange={toggleAll}
                    disabled={eliminableRows.length === 0}
                    title={eliminableRows.length === 0 ? "Sem subscrições elimináveis nesta página" : "Seleccionar todas elimináveis (PE/CA sem pagamentos)"}
                    data-testid="dreams-sub-select-all" />
                </th>
                <th>#</th><th>Cliente</th><th>Enc.</th><th>Plano</th><th>Estado</th>
                <th>Activa até</th><th>Pag.</th><th className="text-right">Acções</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.ID_Assinatura} className="border-t border-slate-100" data-testid={`dreams-sub-row-${a.ID_Assinatura}`}>
                  <td className="py-2">
                    {isEliminable(a) ? (
                      <input type="checkbox"
                        checked={selected.has(a.ID_Assinatura)}
                        onChange={() => toggleOne(a.ID_Assinatura)}
                        data-testid={`dreams-sub-select-${a.ID_Assinatura}`} />
                    ) : null}
                  </td>
                  <td className="py-2 text-slate-500">{a.ID_Assinatura}</td>
                  <td className="max-w-[220px]">
                    <Link to={`/admin/t/cliente/Tinf_Cliente/view/${a.ID_Cliente}`}
                      className="font-medium truncate block hover:text-amber-600 hover:underline"
                      title={`Ver cliente #${a.ID_Cliente}`}
                      data-testid={`dreams-sub-cliente-${a.ID_Assinatura}`}>
                      {a.NomeCompleto}
                    </Link>
                    <div className="text-[11px] text-slate-500 truncate">{a.Email}</div>
                  </td>
                  <td className="whitespace-nowrap">
                    {a.ID_Encomenda ? (
                      <Link to={`/admin/t/encomenda/Tinf_Encomenda/view/${a.ID_Encomenda}`}
                        className="text-amber-600 hover:underline font-medium"
                        title="Ver detalhe da encomenda"
                        data-testid={`dreams-sub-encomenda-${a.ID_Assinatura}`}>
                        #{a.ID_Encomenda}
                      </Link>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td>{PLAN_LABEL[a.Plano] || a.Plano}</td>
                  <td>
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${ESTADO_BADGE[a.Estado] || "bg-slate-100 text-slate-600"}`}
                      title={a.Estado === "CA" ? a.CanceladoMotivo || "" : ""}>
                      {ESTADO_LABEL[a.Estado] || a.Estado}
                    </span>
                  </td>
                  <td className="whitespace-nowrap">
                    {a.Estado === "PG" ? (
                      <button onClick={() => onEditarData(a)} disabled={busy === a.ID_Assinatura}
                        className="group inline-flex items-center gap-1 text-slate-700 hover:text-amber-600"
                        title="Alterar data de validade"
                        data-testid={`dreams-sub-data-${a.ID_Assinatura}`}>
                        {fmt(a.AtivaAte)}
                        <span className="text-[10px] text-slate-400 group-hover:text-amber-600">✎</span>
                      </button>
                    ) : fmt(a.AtivaAte)}
                  </td>
                  <td className="text-center">{a.PagamentosConfirmados}</td>
                  <td className="text-right whitespace-nowrap space-x-1">
                    {a.Estado !== "PG" && (
                      <button onClick={() => onAtivar(a)} disabled={busy === a.ID_Assinatura}
                        className="px-2 py-1 rounded-md bg-emerald-600 text-white text-xs hover:bg-emerald-500 disabled:opacity-50"
                        data-testid={`dreams-sub-ativar-${a.ID_Assinatura}`}>Activar</button>
                    )}
                    {a.Estado === "PG" && (
                      <button onClick={() => onEstender(a)} disabled={busy === a.ID_Assinatura}
                        className="px-2 py-1 rounded-md bg-amber-500 text-white text-xs hover:bg-amber-400 disabled:opacity-50"
                        data-testid={`dreams-sub-estender-${a.ID_Assinatura}`}>Estender</button>
                    )}
                    {a.Estado !== "CA" && (
                      <button onClick={() => onCancelar(a)} disabled={busy === a.ID_Assinatura}
                        className="px-2 py-1 rounded-md bg-rose-600 text-white text-xs hover:bg-rose-500 disabled:opacity-50"
                        data-testid={`dreams-sub-cancelar-${a.ID_Assinatura}`}>Cancelar</button>
                    )}
                    {(a.Estado === "PE" || a.Estado === "CA") && (
                      <button onClick={() => onEliminar(a)} disabled={busy === a.ID_Assinatura}
                        className="px-2 py-1 rounded-md border border-rose-300 text-rose-600 text-xs hover:bg-rose-50 disabled:opacity-50"
                        title="Eliminar definitivamente (irreversível)"
                        data-testid={`dreams-sub-eliminar-${a.ID_Assinatura}`}>Eliminar</button>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={9} className="py-5 text-center text-slate-400">Sem subscrições para este filtro.</td></tr>
              )}
            </tbody>
          </table>
          <div className="flex flex-wrap items-center justify-between gap-2 mt-3 pt-3 border-t border-slate-100"
            data-testid="dreams-subs-pagination">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span>{total} subscrição(ões)</span>
              <select value={pageSize}
                onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(1); }}
                className="border border-slate-200 rounded-md px-1.5 py-1 bg-white"
                data-testid="dreams-subs-page-size">
                {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{n} / pág.</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                className="px-2.5 py-1 rounded-md border border-slate-200 text-xs disabled:opacity-40 hover:bg-slate-50"
                data-testid="dreams-subs-prev">← Anterior</button>
              <span className="text-xs text-slate-600">Página {page} de {totalPages}</span>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                className="px-2.5 py-1 rounded-md border border-slate-200 text-xs disabled:opacity-40 hover:bg-slate-50"
                data-testid="dreams-subs-next">Seguinte →</button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

function eur(v) { return Number(v || 0).toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + "€"; }

function TrialCampaignManager() {
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [showPreview, setShowPreview] = useState(false);

  const load = async () => {
    try {
      setErr(null);
      const { data } = await axios.get(`${BASE}/api/dreams/admin/trial-campaign`, authHeaders());
      setInfo(data);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
  };
  useEffect(() => { load(); }, []);

  const camp = info?.campanha || {};
  const importing = camp.state === "importing" || camp.state === "sending";
  useEffect(() => {
    if (!importing) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [importing]);

  const call = async (path, payload, label) => {
    setBusy(label);
    try {
      const { data } = await axios.post(`${BASE}/api/dreams/admin/trial-campaign/${path}`, payload, authHeaders());
      if (data?.success) alert("Feito.");
      await load();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
    finally { setBusy(""); }
  };

  const ESTADO = {
    idle: "Não preparada", importing: "A importar público…", ready: "Pronta a enviar",
    sending: "A enviar…", sent: "Enviada", error: "Erro",
    scheduled: "Envio faseado activo", paused: "Em pausa", completed: "Concluída",
  };
  const faseado = camp.mode === "phased";
  const restantes = Math.max(0, (camp.audience_now ?? 0));
  const [lote, setLote] = useState(5000);

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="trial-campaign-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-sm font-semibold text-slate-700">Campanha · convite aos 3 meses grátis</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Clientes activos, com RGPD ok, com Newsletter ou Pensamento do Dia activos e sem qualquer subscrição Dreams. Envio via Moosend.
          </p>
        </div>
        <span className="text-[11px] px-2 py-1 rounded-full bg-slate-100 text-slate-600" data-testid="trial-campaign-state">
          {ESTADO[camp.state] || camp.state || "—"}
        </span>
      </div>

      {err && <p className="text-xs text-rose-600 mt-3" data-testid="trial-campaign-error">{err}</p>}

      <div className="grid sm:grid-cols-3 gap-3 mt-4">
        <div className="rounded-lg bg-slate-50 p-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Público elegível</p>
          <p className="text-xl font-bold text-slate-800" data-testid="trial-campaign-audience">
            {(camp.audience_now ?? 0).toLocaleString("pt-PT")}
          </p>
          <p className="text-[10px] text-slate-500 mt-0.5">Newsletter ou Pensamento do Dia activos</p>
        </div>
        <div className="rounded-lg bg-slate-50 p-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">
            {faseado ? "Enviados (lotes)" : "Importados p/ Moosend"}
          </p>
          <p className="text-xl font-bold text-slate-800" data-testid="trial-campaign-imported">
            {faseado
              ? `${(camp.total_sent ?? 0).toLocaleString("pt-PT")} · ${camp.batches_sent ?? 0} lote${(camp.batches_sent ?? 0) === 1 ? "" : "s"}`
              : `${(camp.imported ?? 0).toLocaleString("pt-PT")}${camp.audience_total ? ` / ${camp.audience_total.toLocaleString("pt-PT")}` : ""}`}
          </p>
          {faseado && (
            <p className="text-[10px] text-slate-500 mt-0.5">
              Faltam {restantes.toLocaleString("pt-PT")} · {camp.batch_size?.toLocaleString("pt-PT")}/dia às 09:20
            </p>
          )}
        </div>
        <div className="rounded-lg bg-slate-50 p-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Enviados · aberturas</p>
          <p className="text-xl font-bold text-slate-800" data-testid="trial-campaign-opens">
            {camp.moosend ? `${camp.moosend.Sent ?? 0} · ${camp.moosend.Opened ?? 0}` : "—"}
          </p>
        </div>
      </div>

      {camp.error && <p className="text-xs text-rose-600 mt-3">{camp.error}</p>}

      <div className="flex flex-wrap items-center gap-2 mt-4">
        <button
          onClick={() => {
            if (window.confirm(`Preparar campanha para ${(camp.audience_now ?? 0).toLocaleString("pt-PT")} destinatários? (só cria a lista e o rascunho — não envia)`))
              call("prepare", { force: camp.state === "error" }, "prepare");
          }}
          disabled={busy || importing || camp.state === "sending"}
          data-testid="trial-campaign-prepare"
          className="px-4 py-2 rounded-lg bg-slate-800 text-white text-xs font-semibold disabled:opacity-50"
        >
          {busy === "prepare" ? "A preparar…" : camp.state === "idle" ? "Preparar campanha" : "Preparar de novo"}
        </button>

        <input
          value={testEmail}
          onChange={e => setTestEmail(e.target.value)}
          placeholder="email para teste"
          data-testid="trial-campaign-test-email"
          className="px-3 py-2 rounded-lg border border-slate-200 text-xs w-56"
        />
        <button
          onClick={() => call("test", { emails: [testEmail] }, "test")}
          disabled={busy || camp.state !== "ready" || !testEmail.includes("@")}
          data-testid="trial-campaign-test"
          className="px-4 py-2 rounded-lg border border-slate-300 text-xs font-semibold disabled:opacity-50"
        >
          {busy === "test" ? "A enviar…" : "Enviar teste"}
        </button>

        <button
          onClick={() => {
            const n = (camp.imported ?? 0).toLocaleString("pt-PT");
            if (window.confirm(`ENVIAR a campanha a ${n} pessoas agora? Esta acção não pode ser revertida.`))
              call("send", { confirmacao: "SIM" }, "send");
          }}
          disabled={busy || camp.state !== "ready"}
          data-testid="trial-campaign-send"
          className="px-4 py-2 rounded-lg bg-rose-600 text-white text-xs font-semibold disabled:opacity-50"
        >
          {busy === "send" ? "A enviar…" : "Enviar campanha"}
        </button>

        <button
          onClick={() => setShowPreview(v => !v)}
          data-testid="trial-campaign-preview-toggle"
          className="px-4 py-2 rounded-lg border border-slate-300 text-xs font-semibold"
        >
          {showPreview ? "Esconder pré-visualização" : "Pré-visualizar email"}
        </button>
      </div>

      <p className="text-[11px] text-slate-500 mt-2">
        Assunto: <strong>{info?.assunto || "—"}</strong> · Remetente: {info?.remetente || "—"}
        {camp.sent_at ? ` · Enviada em ${new Date(camp.sent_at).toLocaleString("pt-PT")}` : ""}
      </p>

      <div className="mt-5 pt-4 border-t border-slate-100">
        <p className="text-xs font-semibold text-slate-700">Envio faseado (recomendado)</p>
        <p className="text-[11px] text-slate-500 mt-0.5">
          Um lote por dia às 09:20 até esgotar o público — protege a reputação do domínio.
          Cada lote é uma lista + campanha próprias no Moosend e quem já activou o trial é excluído automaticamente.
        </p>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <label className="text-[11px] text-slate-600">Destinatários por dia</label>
          <input
            type="number" min="100" max="20000" step="100"
            value={lote}
            onChange={e => setLote(parseInt(e.target.value, 10) || 0)}
            data-testid="trial-campaign-batch-size"
            className="px-3 py-2 rounded-lg border border-slate-200 text-xs w-28"
          />
          <button
            onClick={() => {
              const dias = Math.ceil((camp.audience_now ?? 0) / Math.max(1, lote));
              if (window.confirm(`Programar envio de ${lote.toLocaleString("pt-PT")} emails/dia? São cerca de ${dias} dia(s) para cobrir ${(camp.audience_now ?? 0).toLocaleString("pt-PT")} pessoas.`))
                call("schedule", { lote }, "schedule");
            }}
            disabled={busy || camp.state === "sending" || importing}
            data-testid="trial-campaign-schedule"
            className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-xs font-semibold disabled:opacity-50"
          >
            {busy === "schedule" ? "A programar…" : faseado ? "Reprogramar" : "Programar envio faseado"}
          </button>
          <button
            onClick={() => {
              if (window.confirm(`Enviar o próximo lote de ${(camp.batch_size ?? lote).toLocaleString("pt-PT")} emails agora?`))
                call("run-batch", {}, "run-batch");
            }}
            disabled={busy || camp.state !== "scheduled"}
            data-testid="trial-campaign-run-batch"
            className="px-4 py-2 rounded-lg bg-slate-800 text-white text-xs font-semibold disabled:opacity-50"
          >
            {busy === "run-batch" ? "A enviar lote…" : "Enviar lote agora"}
          </button>
          {faseado && camp.state === "scheduled" && (
            <button
              onClick={() => call("pause", {}, "pause")}
              disabled={busy}
              data-testid="trial-campaign-pause"
              className="px-4 py-2 rounded-lg border border-slate-300 text-xs font-semibold disabled:opacity-50"
            >
              Pausar
            </button>
          )}
          {faseado && camp.state === "paused" && (
            <button
              onClick={() => call("pause", { retomar: true }, "pause")}
              disabled={busy}
              data-testid="trial-campaign-resume"
              className="px-4 py-2 rounded-lg border border-emerald-400 text-emerald-700 text-xs font-semibold disabled:opacity-50"
            >
              Retomar
            </button>
          )}
        </div>

        {faseado && (camp.history || []).length > 0 && (
          <table className="w-full text-xs mt-4" data-testid="trial-campaign-history">
            <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
              <th className="py-1">Lote</th><th>Enviados</th><th>Quando</th>
            </tr></thead>
            <tbody>
              {[...camp.history].reverse().slice(0, 10).map(h => (
                <tr key={h.lote} className="border-t border-slate-100">
                  <td className="py-1.5">#{h.lote}</td>
                  <td>{(h.enviados || 0).toLocaleString("pt-PT")}</td>
                  <td className="text-slate-500">{new Date(h.em).toLocaleString("pt-PT")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showPreview && info?.html && (
        <iframe
          title="Pré-visualização"
          data-testid="trial-campaign-preview"
          srcDoc={info.html}
          className="w-full h-[560px] mt-4 rounded-lg border border-slate-200 bg-white"
        />
      )}
    </section>
  );
}

export default function AdminDreams() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("ihtp_token");
        const { data } = await axios.get(`${BASE}/api/dreams/admin/metrics`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true });
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <p className="text-slate-400">A carregar métricas...</p>;
  if (err) return <p className="text-rose-400">{err}</p>;
  if (!data) return null;

  const k = data.kpis;
  const planLabel = { M: "Mensal", T: "Trimestral", S: "Semestral", A: "Anual" };

  return (
    <div className="space-y-6 p-1" data-testid="admin-dreams">
      <header className="flex items-center gap-3">
        <Sparkles size={22} className="text-amber-500" />
        <div>
          <h1 className="text-2xl font-bold">IHTP Dreams — Métricas</h1>
          <p className="text-xs text-slate-500">Visão executiva da sub-app de acompanhamento virtual.</p>
        </div>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Subscritores activos" value={k.active_subs} icon={Users} accent="emerald" />
        <Kpi label="MRR" value={eur(k.mrr_eur)} icon={TrendingUp} accent="amber" hint={`ARR ${eur(k.arr_eur)}`} />
        <Kpi label="Sonhos criados" value={k.sonhos_total} icon={Star} hint={`+${k.sonhos_30d} nos últimos 30d`} />
        <Kpi label="Tarefas concluídas" value={k.tarefas_concluidas} hint={`+${k.tarefas_concluidas_30d} nos últimos 30d`} />
        <Kpi label="Sonhos concluídos" value={k.sonhos_concluidos} icon={Trophy} />
        <Kpi label="Total sonhadores" value={k.total_sonhadores} hint="todos os tempos" />
        <Kpi label="Shares views" value={k.shares_vistas} icon={Share2} hint={`${k.shares_total} links criados`} />
        <Kpi label="Pagamentos pendentes" value={k.pagamentos_pendentes} icon={Clock} accent={k.pagamentos_pendentes ? "amber" : "slate"} />
      </section>

      <TrialCampaignManager />

      <SubscricoesManager />

      <section className="grid lg:grid-cols-2 gap-4">
        <Card title="Distribuição por plano">
          {data.by_plan.length === 0 ? <p className="text-slate-500 text-sm">Ainda sem dados.</p> : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-500"><th>Plano</th><th className="text-right">Subscritores</th></tr></thead>
              <tbody>
                {data.by_plan.map(r => (
                  <tr key={r.Plano} className="border-t border-slate-100">
                    <td className="py-2">{planLabel[r.Plano] || r.Plano}</td>
                    <td className="text-right font-semibold">{r.N}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="Novas subscrições (últimos 12 meses)">
          {data.by_month.length === 0 ? <p className="text-slate-500 text-sm">Ainda sem dados.</p> : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-500"><th>Mês</th><th className="text-right">N</th><th className="text-right">Receita</th></tr></thead>
              <tbody>
                {data.by_month.map(r => (
                  <tr key={r.Mes} className="border-t border-slate-100">
                    <td className="py-2">{r.Mes}</td>
                    <td className="text-right">{r.N}</td>
                    <td className="text-right font-semibold">{eur(r.Receita)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="Top 5 Sonhadores">
          {data.top_sonhadores.length === 0 ? <p className="text-slate-500 text-sm">Sem ranking ainda.</p> : (
            <ol className="space-y-2">
              {data.top_sonhadores.map((r, i) => (
                <li key={i} className="flex items-center gap-3 text-sm">
                  <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                  <span className="flex-1 truncate">{r.nome}</span>
                  <span className="text-xs text-slate-500">{r.concl}t · {r.sonhos}s</span>
                  <span className="font-bold text-amber-600">{r.pontos}⭐</span>
                </li>
              ))}
            </ol>
          )}
        </Card>

        <Card title="Top 10 Shares Dream Board">
          {data.top_shares.length === 0 ? <p className="text-slate-500 text-sm">Nenhuma view ainda.</p> : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-500"><th>Sonhador</th><th className="text-right">Vistas</th><th className="text-right text-[10px]">Última</th></tr></thead>
              <tbody>
                {data.top_shares.map(r => (
                  <tr key={r.Token} className="border-t border-slate-100">
                    <td className="py-2 truncate max-w-[200px]">{r.NomeCompleto}</td>
                    <td className="text-right font-semibold">{r.Vistas}</td>
                    <td className="text-right text-[10px] text-slate-500">{r.UltimaVistaEm ? new Date(r.UltimaVistaEm).toLocaleDateString("pt-PT") : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </section>
    </div>
  );
}

function Kpi({ label, value, hint, icon: Icon, accent }) {
  const color = accent === "emerald" ? "text-emerald-600" : accent === "amber" ? "text-amber-600" : "text-slate-700";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500">
        {Icon && <Icon size={14} className={color} />}
        {label}
      </div>
      <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
      {hint && <p className="text-[11px] text-slate-500 mt-0.5">{hint}</p>}
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <h2 className="text-sm font-semibold text-slate-700 mb-3">{title}</h2>
      {children}
    </div>
  );
}
